/**
 * Pure formatting helpers for the course screens.
 *
 * Kept apart from the components so they can be tested: anything that imports
 * a component pulls in CCText, whose own '@/'-aliased imports jest cannot
 * resolve.
 */

/** "1h 33m" / "47m". Empty string when the length is unknown. */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds / 60);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (!hours) return `${mins}m`;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Where a square sits on the board, in pixels from its top-left corner.
 *
 * Used to draw the hint marker as an overlay. It cannot be drawn by passing
 * `customSquareStyles` to chessboard-package: that prop is spread last onto the
 * inner board, so it replaces the package's own square styling wholesale —
 * taking the selection highlight and the last-move highlight with it, which
 * leaves clicking a piece with no visible response at all.
 */
export function squareOffset(
  square: string,
  boardSize: number,
  orientation: 'white' | 'black' = 'white',
): {left: number; top: number; size: number} | null {
  if (!/^[a-h][1-8]$/.test(square || '')) return null;
  if (!Number.isFinite(boardSize) || boardSize <= 0) return null;

  const size = boardSize / 8;
  const file = square.charCodeAt(0) - 'a'.charCodeAt(0); // a=0 … h=7
  const rank = Number(square[1]) - 1; // 1=0 … 8=7

  // From white's side a1 is bottom-left; flipping the board mirrors both axes.
  const col = orientation === 'white' ? file : 7 - file;
  const rowFromBottom = orientation === 'white' ? rank : 7 - rank;

  return {left: col * size, top: (7 - rowFromBottom) * size, size};
}

/** "27:50" — where in the recording a position was shown. */
export function formatTimestamp(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '';
  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const mins = Math.floor((whole % 3600) / 60);
  const secs = whole % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours ? `${hours}:${pad(mins)}:${pad(secs)}` : `${mins}:${pad(secs)}`;
}
